/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import t from "../../../core/Collection.js";
import { isCGCS2000 as n, isEarth as e } from "../../../geometry/support/spatialReferenceUtils.js";
import { getTileMaxtrixSetFromActiveLayer as r } from "../../../layers/support/layerUtils.js";
import { ViewingMode as o } from "../../ViewingMode.js";
import { ImageWithType as l } from "../support/StreamDataLoader.js";
import { NeighborIndex as i } from "./ITile.js";
import { t as u } from "../../../chunks/terrainUtilsPlanar.js";
import { t as f } from "../../../chunks/terrainUtilsSpherical.js";
const c = {
  [o.Global]: f,
  [o.Local]: u
};
function a(t, n) {
  t || console.warn("Terrain: " + n);
}
let T = !1,
  s = !1;
function S(t) {
  s = t, T = T || t;
}
function E(t) {
  T = t;
}
function p(t, n) {
  if (T && !t) {
    const t = new Error().stack?.slice(5);
    throw console.warn("Terrain internal: " + (n ?? "") + " at " + t), new Error("Assertion failed" + (n ? ": " + n : ""));
  }
}
function m(t) {
  return O(t) ? {
    fullExtent: t.fullExtent,
    minScale: t.layer.minScale,
    maxScale: t.layer.maxScale,
    tilemapCache: null
  } : t.layer;
}
function H(t) {
  return "imagery-tile" === t?.type || "wcs" === t?.type;
}
function O(t) {
  return "imagery-tile-3d" === t?.type;
}
function y(t) {
  return "tile-3d" === t?.type;
}
function _(t) {
  return "vector-tile-3d" === t?.type;
}
function I(t) {
  return "wmts-3d" === t?.type;
}
function R(t) {
  return "elevation-3d" === t?.type;
}
function d(t) {
  return "group" === t?.type;
}
function U(t) {
  return t && (y(t) || I(t) || O(t) || _(t));
}
function W(t) {
  return t && (y(t) || O(t) || _(t) || I(t));
}
function N(t) {
  return W(t) || R(t);
}
function x(t) {
  const n = t?.sourceLayerInfo?.data;
  return null != n && "type" in n && "raster-tile" === n.type;
}
function A(t) {
  return h(t?.sourceLayerInfo) || !!t?.isVTLBackground;
}
function g(t) {
  const n = t?.sourceLayerInfo?.data;
  return null != n && "type" in n && "tile-texture" === n.type;
}
function L(t) {
  const n = t?.sourceLayerInfo?.data;
  return n instanceof HTMLImageElement || n instanceof l || n instanceof HTMLCanvasElement || n instanceof ImageData;
}
function h(t) {
  return null != t?.data && "type" in t.data && "vector-tile" === t.data.type;
}
function w(t) {
  return null != t && "release" in t && t.release(), null;
}
function j(t) {
  return t.fetchTile && !1 !== t.hasOverriddenFetchTile;
}
function C(t, n, e, r) {
  return c[r].checkIfTileInfoSupportedForViewSR(t, e, n);
}
function b(t, n, e) {
  let r = null,
    l = null;
  if ("wmts" === t?.type) {
    const o = v(t, n, e);
    r = o.tileInfo, l = o.fullExtent;
  } else {
    l = H(t) ? t.getCompatibleFullExtent(n) : t.fullExtent;
    const i = e === o.Local;
    if (H(t)) r = t.getCompatibleTileInfo(n, l, i);else if ("vector-tile" === t?.type) {
      const e = i && !k(n) || M.force512VTL,
        o = t.tileInfo.spatialReference.isGeographic;
      r = e ? t.tileInfo : t.tileInfo.getOrCreateCompatible(256, o ? 1 : 2);
    } else r = t.tileInfo;
  }
  return null != r && null != l && null == C(r, l, n, e) ? {
    tileInfo: r,
    fullExtent: l
  } : null;
}
function v(n, e, o) {
  const l = r(n);
  if (null != l) {
    if (!t.isCollection(l)) return {
      tileInfo: l.tileInfo,
      fullExtent: l.fullExtent
    };
    {
      const t = l.find(t => null == C(t.tileInfo, t.fullExtent, e, o));
      if (t) return {
        tileInfo: t.tileInfo,
        fullExtent: t.fullExtent
      };
    }
  }
  return {
    tileInfo: null,
    fullExtent: null
  };
}
function k(t) {
  return t.isWGS84 || t.isWebMercator || n(t) || !e(t);
}
const M = {
  force512VTL: !1
};
function V(t) {
  return "[" + t[0] + "," + t[1] + "," + t[2] + "]";
}
function F(t) {
  return "(" + t[0] + "," + t[1] + "," + t[2] + ")";
}
function G(t, n, e = Z) {
  return Math.abs(t - n) < e;
}
function D(t) {
  return t === i.NORTH_EAST ? i.SOUTH_WEST : t === i.NORTH_WEST ? i.SOUTH_EAST : t === i.SOUTH_WEST ? i.NORTH_EAST : i.NORTH_WEST;
}
function B(t) {
  return t === i.NORTH ? i.SOUTH : t === i.EAST ? i.WEST : t === i.SOUTH ? i.NORTH : i.EAST;
}
function P(t) {
  return t === i.NORTH_WEST || t === i.SOUTH_WEST;
}
function q(t) {
  return t === i.NORTH_WEST || t === i.NORTH_EAST;
}
function z(t) {
  return t === i.NORTH_WEST || t === i.WEST || t === i.SOUTH_WEST;
}
function J(t) {
  return t === i.NORTH_EAST || t === i.EAST || t === i.SOUTH_EAST;
}
function K(t) {
  return t === i.SOUTH_EAST || t === i.SOUTH || t === i.SOUTH_WEST;
}
function Q(t) {
  return t === i.NORTH_EAST || t === i.NORTH || t === i.NORTH_WEST;
}
const X = [i.NORTH, i.EAST, i.SOUTH, i.WEST],
  Y = [i.NORTH_EAST, i.SOUTH_EAST, i.SOUTH_WEST, i.NORTH_WEST],
  Z = 1e-5;
export { G as almostEquals, C as checkIfTileInfoSupportedForView, E as enableInternalTerrainChecks, T as enableTerrainInternalChecks, S as enableTerrainWaterproofnessChecks, s as enableWaterproofnessTests, Z as eps, m as getLayerWithExtentRange, v as getTileInfoAndExtentFromWMTSLayer, b as getTiledLayerInfo, p as internalAssert, U as isBlendableLayerView, J as isEast, R as isElevationLayerView, d as isGroupLayerView, O as isImageryTileLayerView, x as isImageryTileRenderInfo, W as isMapTileLayerView, Q as isNorth, q as isNorthCorner, H as isProjectableRasterLayer, L as isRasterTileRenderInfo, K as isSouth, N as isSurfaceLayerView, g as isTextureTileRenderInfo, y as isTileLayerView, _ as isVectorTileLayerView, h as isVectorTilePerLayerInfo, A as isVectorTileRenderInfo, I as isWMTSLayerView, z as isWest, P as isWestCorner, V as lij2s, Y as neighborCornerIndices, X as neighborEdgeIndices, D as oppositeCorner, B as oppositeEdge, w as releaseTileData, M as test, j as useFetchTileForLayer, F as v32s, k as vtlAssumes256PixelSizeAsDefault, a as weakAssert };